export type BillingPlan = "annual" | "enterprise";

export type BillingStep = "qualification" | "checkout" | "demo";

export type SubscriptionStatus =
  "checkout_started" | "active" | "scheduled_cancel" | "canceled" | "expired";

export const REPORTING_STANDARDS = [
  "ghg_protocol",
  "sbti",
  "re100",
  "cdp",
  "csrd",
  "iso_14064",
  "ecovadis",
  "cbam",
  "other",
] as const;

export type ReportingStandard = (typeof REPORTING_STANDARDS)[number];

export const CONSUMPTION_BANDS = ["under_10_gwh", "over_10_gwh"] as const;

export type ConsumptionBand = (typeof CONSUMPTION_BANDS)[number];

export const COUNTRY_SCOPES = ["one", "multiple"] as const;

export type CountryScope = (typeof COUNTRY_SCOPES)[number];

export const CONSUMPTION_REGIONS = [
  "europe",
  "usa",
  "china",
  "japan",
  "rest_of_world",
] as const;

export type ConsumptionRegion = (typeof CONSUMPTION_REGIONS)[number];

/**
 * A company's software subscription as `GET /api/billing/companies/{id}/status` answers it.
 *
 * @property prompt_required - True while the dashboard must show the onboarding modal
 * @property step - The modal step still to complete; null once the company is done (or exempt)
 * @property price - The self-serve plan's price; null for enterprise and before qualification
 * @property consumption_band - Saved as soon as it is picked, so it can be set while `plan` is still null
 * @property demo_requested_at - When an enterprise prospect opened the booking page; the modal then waits on sales
 */
export interface BillingStatus {
  company_id: number;
  prompt_required: boolean;
  step: BillingStep | null;
  plan: BillingPlan | null;
  price: { amount_eur: string; interval: "year" } | null;
  consumption_band: ConsumptionBand | null;
  country_scope: CountryScope | null;
  reporting_standards: ReportingStandard[];
  reporting_standard_other: string | null;
  consumption_regions: ConsumptionRegion[];
  subscription_status: SubscriptionStatus | null;
  current_period_end: string | null;
  /** Paid up by the backend's reckoning, period end and renewal grace included. */
  paid: boolean;
  demo_url: string;
  demo_requested_at: string | null;
  onboarding_call_due: boolean;
  onboarding_call_requested_at: string | null;
  onboarding_call_booked_at: string | null;
  onboarding_call_url: string;
}

export interface QualificationAnswers {
  consumption_band: ConsumptionBand;
  country_scope: CountryScope;
  reporting_standards: ReportingStandard[];
  reporting_standard_other: string | null;
  consumption_regions: ConsumptionRegion[];
}

/** Answers saved as the user goes: only the fields present are changed, and null clears one. */
export type QualificationDraft = Partial<{
  consumption_band: ConsumptionBand | null;
  country_scope: CountryScope | null;
  reporting_standards: ReportingStandard[];
  reporting_standard_other: string | null;
  consumption_regions: ConsumptionRegion[];
}>;

/** The onboarding call's progress: the booking page was opened, then the customer confirmed a time. */
export type OnboardingCallState = "requested" | "booked";

/** Which role the demo booking page preselects (its `?organisation=` parameter). */
export type DemoOrganisation = "corporate" | "trader";

/** The admin panel's view of the same row, plus the date until which the prompt is switched off. */
export interface AdminBillingStatus extends BillingStatus {
  billing_prompt_disabled_until: string | null;
  qualified_at: string | null;
  creem_customer_id: string | null;
  creem_subscription_id: string | null;
}
